import { useEffect, useLayoutEffect, useState } from 'react'

import Action from 'ageas-react-component-action'
import GreyBackgroundBox from 'ageas-react-component-greybackground'
import MiniLoader from 'ageas-react-component-miniloader'

import { DigitalSignaturesSelection } from '../../Components/Confirmation/DigitalSignaturesSelection'
import { EmissionResult } from '../../Components/Confirmation/EmissionResult'
import { ManualSignaturesSelection } from '../../Components/Confirmation/ManualSignaturesSelection'
import { PaymentOptions } from '../../Components/Confirmation/PaymentOptions'
import { PolicyDocumentation } from '../../Components/Confirmation/PolicyDocumentation'
import {
  DocumentationContainer,
  ExpandedLoading,
} from '../../Components/Confirmation/styles'
import { ENV } from '../../Environment'
import { MainContainer } from '../../GlobalStyles'
import { useDigitalSignatureSettingsStore } from '../../Stores/digitalSignature/useDigitalSignatureSettingsStore'
import { usePaymentConfirmationStore } from '../../Stores/paymentConfirmationStore'
import { useProgressStore } from '../../Stores/progressStore'
import { useCancelDigitalSignatureProcessStore } from '../../Stores/useCancelDigitalSignatureProcessStore'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { usePolicyDocumentsSelectedStore } from '../../Stores/usePolicyDocumentsSelectedStore'
import { useRefreshDigitalSignatureStatusStore } from '../../Stores/useRefreshDigitalSignatureStatusStore'
import { useRefreshFirstReceiptStatusStore } from '../../Stores/useRefreshFirstReceiptStatusStore'
import { useResumePetisSimulationProposalStore } from '../../Stores/useResumePetisSimulationProposalStore'
import { useSimulationSummaryDownloadStore } from '../../Stores/useSimulationSummaryDownloadStore'
import { useSubmitProposalStore } from '../../Stores/useSubmitProposalStore'
import { INVOICE_STATUS } from '../../constants/invoiceStatus'
import { useGetDigitalSignatureSettings } from '../../hooks/api/digitalSignature/useGetDigitalSignatureSettings'
import { useRefreshFirstReceiptStatus } from '../../hooks/api/payment/useRefreshFirstReceiptStatus'
import { useRefreshFirstReceiptStatusPayload } from '../../hooks/api/payment/useRefreshFirstReceiptStatusPayload'
import { useSendPolicyDocumentsByEmail } from '../../hooks/api/sendPolicyDocumentsByEmail/useSendPolicyDocumentsByEmail'
import { useSendPolicyDocumentsByEmailPayload } from '../../hooks/api/sendPolicyDocumentsByEmail/useSendPolicyDocumentsByEmailPayload'
import { useSendDigitalSignatureHydration } from '../../hooks/hydration/useSendDigitalSignatureHydration'
import { useErrorRequest } from '../../hooks/useErrorRequest'
import { t } from '../../i18n'
import { removeDuplicatesDocuments } from '../../utils/validates'
import { FormalizationTableContainer } from '../Formalization/style'
import { ActionsContainer, Container, ContainerActionUpdate } from './styles'

export const Confirmation = () => {
  const [forceRefreshPaymentMethodsList, setForceRefreshPaymentMethodsList] =
    useState(true)
  const [resetPaymentOptions, setResetPaymentOptions] = useState(false)
  const { handleErrorRequest } = useErrorRequest()
  const { setCurrentStep } = useProgressStore()
  const { isSignatureDigital, setIsFirstReceiptPaid, isFirstReceiptPaid } =
    usePaymentConfirmationStore()
  const { setSettings, settings } = useDigitalSignatureSettingsStore()
  const { createProposal } = useCreateProposalStore()
  const { refreshFirstReceiptStatus } = useRefreshFirstReceiptStatusStore()
  const { payload: refreshFirstReceiptStatusPayload } =
    useRefreshFirstReceiptStatusPayload()
  const { payload: sendPolicyDocumentsByEmailPayload } =
    useSendPolicyDocumentsByEmailPayload()
  const { policyDocuments } = usePolicyDocumentsSelectedStore()
  const { submitProposal } = useSubmitProposalStore()
  const { simulationSummaryDownload } = useSimulationSummaryDownloadStore()
  const { generateProposalDocuments } = useGenerateProposalDocumentsStore()
  const { resumePetisSimulationProposal } =
    useResumePetisSimulationProposalStore()
  const { cancelDigitalSignatureProcess } =
    useCancelDigitalSignatureProcessStore()
  const { handleCurrentSettingsSelected } = useSendDigitalSignatureHydration()
  const {
    isSigned,
    isSignedResume,
    isLoadingRefreshDigitalSignatureStatus,
    refreshDigitalSignatureStatus,
  } = useRefreshDigitalSignatureStatusStore()
  const {
    data: digitalSignatureSettingsData,
    isLoading: digitalSignatureSettingsIsLoading,
    isSuccess: digitalSignatureSettingsIsSuccess,
    isError: digitalSignatureSettingsIsError,
    error: digitalSignatureSettingsError,
  } = useGetDigitalSignatureSettings(
    {
      policyId: createProposal?.createProposalResponse?.proposalId || '',
    },
    isSignatureDigital || settings?.signatureSettings?.length === 0,
  )

  const isInvoiceOffer =
    (refreshFirstReceiptStatus?.invoiceStatus ||
      cancelDigitalSignatureProcess?.firstReceiptInfo?.status ||
      refreshDigitalSignatureStatus?.firstReceiptInfo?.status ||
      submitProposal?.firstReceiptInfo?.status) === INVOICE_STATUS.OFFER

  const hasDocuments =
    removeDuplicatesDocuments(
      generateProposalDocuments?.documentstoList ||
        simulationSummaryDownload?.documentsToList ||
        [],
      'keyRef',
    ).length > 0 || (submitProposal?.documents || [])?.length > 0

  const {
    mutate: sendPolicyDocumentsByEmailMutate,
    isLoading: sendPolicyDocumentsByEmailIsLoading,
  } = useSendPolicyDocumentsByEmail()
  const {
    mutate: refreshFirstReceiptStatusMutate,
    isSuccess: refreshFirstReceiptStatusIsSuccess,
    isLoading: refreshFirstReceiptStatusIsLoading,
  } = useRefreshFirstReceiptStatus()

  const redirectToURL = () => {
    const targetURL = ENV().SimulationRedirectUrl
    window.location.href = targetURL
  }

  const handleSubmitPayFirstReceipt = () => {
    redirectToURL()
  }

  const handleRefreshFirstReceiptStatus = () => {
    refreshFirstReceiptStatusMutate(refreshFirstReceiptStatusPayload)
  }

  useEffect(() => {
    if (digitalSignatureSettingsIsError) {
      handleErrorRequest({ error: digitalSignatureSettingsError })
    }
  }, [
    digitalSignatureSettingsError,
    handleErrorRequest,
    digitalSignatureSettingsIsError,
  ])

  useLayoutEffect(() => {
    setCurrentStep(7)
  }, [setCurrentStep])

  useEffect(() => {
    if (
      refreshFirstReceiptStatusIsSuccess &&
      refreshFirstReceiptStatus?.invoiceStatus === INVOICE_STATUS.ANNULLED
    ) {
      setForceRefreshPaymentMethodsList(false)

      // Workaround to force refresh payment methods list when invoice status is annulled
      setTimeout(() => {
        setForceRefreshPaymentMethodsList(true)
      }, 100)
    } else if (
      refreshFirstReceiptStatus?.invoiceStatus === INVOICE_STATUS.PAID
    ) {
      setIsFirstReceiptPaid(true)
    }
  }, [
    refreshFirstReceiptStatusIsSuccess,
    refreshFirstReceiptStatus?.invoiceStatus,
  ])

  useEffect(() => {
    if (digitalSignatureSettingsData && digitalSignatureSettingsIsSuccess) {
      setSettings(digitalSignatureSettingsData)
      handleCurrentSettingsSelected(
        resumePetisSimulationProposal,
        digitalSignatureSettingsData,
      )
    }
  }, [
    digitalSignatureSettingsData,
    digitalSignatureSettingsIsSuccess,
    setSettings,
  ])

  return (
    <MainContainer>
      {isSignedResume && isLoadingRefreshDigitalSignatureStatus && (
        <ExpandedLoading>
          <MiniLoader color="transparent" minHeight="auto" maxWidth="auto" />
        </ExpandedLoading>
      )}
      <GreyBackgroundBox
        id="policy-detail-2"
        version="2"
        colorTheme="blue"
        name={t('pages.step7.emissionResult.label')}
        open={true}
        detail={true}
        components={<EmissionResult />}
      />

      {isSignedResume ? null : (
        <>
          {isSignatureDigital && !isSigned && (
            <FormalizationTableContainer>
              <DigitalSignaturesSelection
                isLoading={digitalSignatureSettingsIsLoading}
              />
            </FormalizationTableContainer>
          )}
        </>
      )}
      {(!isSignatureDigital || isSigned) &&
        !isFirstReceiptPaid &&
        !isInvoiceOffer && (
          <Container>
            {forceRefreshPaymentMethodsList && (
              <PaymentOptions resetPaymentMethod={setResetPaymentOptions} />
            )}
            <ContainerActionUpdate>
              <Action
                id="button-payment-update"
                label={t('general.update')}
                buttonType="secondary"
                type="button"
                disable={refreshFirstReceiptStatusIsLoading}
                onValueChanged={() => {
                  if (resetPaymentOptions) {
                    setResetPaymentOptions(false)
                    setForceRefreshPaymentMethodsList(false)
                    setTimeout(() => {
                      setForceRefreshPaymentMethodsList(true)
                    }, 100)
                  } else {
                    handleRefreshFirstReceiptStatus()
                  }
                }}
              />
            </ContainerActionUpdate>
          </Container>
        )}
      {(!isSignatureDigital || isSigned) && (
        <>
          <FormalizationTableContainer>
            <ManualSignaturesSelection />
          </FormalizationTableContainer>
          <DocumentationContainer>
            <PolicyDocumentation />
          </DocumentationContainer>
          <ActionsContainer>
            <div className="button-group">
              {hasDocuments &&
                (sendPolicyDocumentsByEmailPayload.recipientEmail || '')
                  ?.length > 0 && (
                  <Action
                    id="confirmation-final-send-email"
                    label={t('pages.step7.sendByEmail')}
                    buttonType="secondary"
                    type="button"
                    disable={
                      sendPolicyDocumentsByEmailIsLoading ||
                      policyDocuments.length === 0
                    }
                    onValueChanged={() =>
                      sendPolicyDocumentsByEmailMutate(
                        sendPolicyDocumentsByEmailPayload,
                      )
                    }
                  />
                )}
              <Action
                id="confirmation-final-button"
                label={t('pages.step7.leave')}
                buttonType="primary"
                type="button"
                onValueChanged={handleSubmitPayFirstReceipt}
              />
            </div>
          </ActionsContainer>
        </>
      )}
    </MainContainer>
  )
}
